import { ReactNode } from 'react';
import { Avatar, TableCell, TableRow } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ClientScore, ScoringFunction, SupplierRating } from '../../../../types/advise';
import { formatGeneratedAt } from '../../../../utils/summaryDate';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ClientScoreBar } from './ClientScoreBar';
import { RatingSlider } from './RatingSlider';
import { fullName, initials } from './RequestSupplierScoring.calc';
import { ScoreBadge } from './ScoreBadge';
import { ScoringPills } from './ScoringPills';
import {
  CLIENT_PURPLE,
  isAdjusted,
  isWideSpread,
  responsesLabel,
  scoreSpread,
  WIDE_SPREAD
} from './SupplierScoring.calc';

interface FunctionScoreDetailsProps {
  fn: ScoringFunction;
  rating?: number;
  ratingRow?: SupplierRating;
  clientScore?: ClientScore;
  // Left out for a reader, who sees everything but changes nothing.
  onRate?: (rating: number) => void;
  onRelease?: (rating: number) => void;
  onUseAverage?: () => void;
  onEditNote?: () => void;
  disabled?: boolean;
  resetToken?: number;
}

const Heading = ({ children }: { children: ReactNode }) => (
  <TextBlock
    size="xs"
    color="grey6"
    className="mb-2 uppercase tracking-wide">
    {children}
  </TextBlock>
);

/** Beneath a function's row: the client average and who gave each score, beside the user's own rating and note. */
export const FunctionScoreDetails = ({
  fn,
  rating,
  ratingRow,
  clientScore,
  onRate,
  onRelease,
  onUseAverage,
  onEditNote,
  disabled = false,
  resetToken
}: FunctionScoreDetailsProps) => {
  const adjusted = isAdjusted(rating, clientScore);
  // Blank between a note's save and its answer, which brings who wrote it and when.
  const notedBy = [ratingRow?.noted_by_name, formatGeneratedAt(ratingRow?.noted_at, 'MMM d, h:mm a')]
    .filter(Boolean)
    .join(' · ');

  const yourRating = (
    <div>
      <Heading>Your rating</Heading>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          {onRate ? (
            <RatingSlider
              label={fn.label}
              rating={rating}
              clientAverage={clientScore?.average}
              onRate={onRate}
              onRelease={onRelease}
              disabled={disabled}
              resetToken={resetToken}
            />
          ) : null}
        </div>
        <ScoreBadge score={rating ?? null} />
      </div>
      {clientScore && onUseAverage && rating !== clientScore.average && (
        <TextButton
          disabled={disabled}
          onClick={onUseAverage}>
          Use client average ({clientScore.average})
        </TextButton>
      )}
      {adjusted && !ratingRow?.note && onEditNote && (
        <div className="mt-3">
          <TextButton
            size="xs"
            disabled={disabled}
            onClick={onEditNote}>
            Add note
          </TextButton>
        </div>
      )}
      {adjusted && ratingRow?.note && (
        <div className="mt-3">
          <Heading>Adjustment note</Heading>
          <TextBlock
            size="sm"
            color="grey8">
            “{ratingRow.note}”
          </TextBlock>
          <TextBlock
            size="xs"
            color="grey6"
            className="mt-1">
            {notedBy}
            {onEditNote && (
              <>
                {notedBy && ' · '}
                <TextButton
                  size="xs"
                  disabled={disabled}
                  onClick={onEditNote}>
                  Edit note
                </TextButton>
              </>
            )}
          </TextBlock>
        </div>
      )}
    </div>
  );

  return (
    <TableRow>
      {/* Clients' names and scores, and the note, stay out of session replay. */}
      <TableCell
        colSpan={4}
        data-dd-privacy="mask"
        sx={{ backgroundColor: 'grey.50', borderLeft: 3, borderLeftColor: CLIENT_PURPLE, px: 3, py: 2 }}>
        {clientScore ? (
          <div className="grid gap-6 md:grid-cols-[1fr_1.5fr_1.5fr]">
            <div className="flex flex-col gap-3">
              <Heading>Summary</Heading>
              <div>
                <TextBlock
                  size="xs"
                  color="grey6">
                  Client average
                </TextBlock>
                <TextSpan
                  size="xl"
                  weight="semibold"
                  color="purple">
                  {clientScore.average}
                </TextSpan>{' '}
                <TextSpan
                  size="xs"
                  color="purple">
                  {responsesLabel(clientScore.scores.length)}
                </TextSpan>
              </div>
              <div>
                <TextBlock
                  size="xs"
                  color="grey6">
                  Your rating
                </TextBlock>
                <span className="flex items-center gap-2">
                  <TextSpan
                    size="xl"
                    weight="semibold"
                    color={rating === undefined ? 'grey6' : 'grey8'}>
                    {rating ?? '–'}
                  </TextSpan>
                  <ScoringPills
                    adjusted={adjusted}
                    wideSpread={false}
                  />
                </span>
              </div>
              <div>
                <TextBlock
                  size="xs"
                  color="grey6">
                  Spread
                </TextBlock>
                <span className="flex items-center gap-2">
                  <TextSpan
                    size="xl"
                    weight="semibold"
                    color="grey8">
                    {scoreSpread(clientScore)} points
                  </TextSpan>
                  {isWideSpread(clientScore) ? (
                    <ScoringPills
                      adjusted={false}
                      wideSpread
                    />
                  ) : (
                    <TextSpan
                      size="xs"
                      color="grey6">
                      within range (flag at {WIDE_SPREAD}+)
                    </TextSpan>
                  )}
                </span>
              </div>
            </div>
            <div>
              <Heading>Individual scores · {clientScore.scores.length}</Heading>
              <ul className="flex flex-col divide-y divide-grey-1">
                {clientScore.scores.map((score) => (
                  <li
                    key={score.request_id}
                    className="flex items-center gap-3 py-2">
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 13,
                        color: CLIENT_PURPLE,
                        bgcolor: alpha(CLIENT_PURPLE, 0.1)
                      }}>
                      {initials(score)}
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <TextBlock
                        size="sm"
                        weight="semibold"
                        color="grey8">
                        {fullName(score)}
                      </TextBlock>
                      <TextBlock
                        size="xs"
                        color="grey6">
                        {[score.role, formatGeneratedAt(score.submitted_at, 'MMM d, h:mm a')]
                          .filter(Boolean)
                          .join(' · ')}
                      </TextBlock>
                    </span>
                    <ClientScoreBar
                      score={score.score}
                      label={`${fullName(score)} scored ${score.score}`}
                      width={96}
                    />
                    <ScoreBadge
                      score={score.score}
                      client
                    />
                  </li>
                ))}
              </ul>
            </div>
            {yourRating}
          </div>
        ) : (
          <div className="flex flex-col gap-2 md:max-w-md">
            {yourRating}
            <TextBlock
              size="sm"
              color="grey6">
              No client responses yet
            </TextBlock>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};
